import { memo, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import clsx from 'clsx'
import { ChevronDown, Loader2, Play, Rocket, Upload as UploadIcon, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ProtoDropdown, ProtoInput, ProtoNumberInput } from '@/features/studio/components/ui-kit'
import type { ProtoDropdownOption } from '@/features/studio/components/ui-kit'
import { AudioWavePlayer } from '@/features/studio/components/AudioWavePlayer'
import { ImagePreviewModal } from '@/features/studio/modals/ImagePreviewModal'
import { Button } from '@/shared/components/ui/button'
import { useCanvasNodeContext, type CanvasNodeContextValue } from '@/features/studio/canvas/CanvasNodeContext'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { MediaKindThumb } from '@/features/studio/components/MediaKindThumb'
import { handleColor, handlesOf, handleTooltipKey, handleEdgeOffsetY, uploadOutputHandles, type HandleSpec } from '@/features/studio/canvas/handles'
import { primaryParamFieldsForNode, advancedParamFieldsForNode, resolvedGenParamsForSubmit, type ModelParamField } from '@/features/studio/canvas/model-params'
import { buildCanvasModelOptions } from '@/features/studio/canvas/model-options'
import { formatResolvedApiError, simplifyAITaskErrorMessage } from '@/features/studio/lib/resolveApiError'
import { AudioGenBody } from '@/features/studio/canvas/nodes/AudioGenBody'
import { audioPromptPlaceholderKey, getActiveAudioTab } from '@/features/studio/canvas/audio-tabs'
import {
  NodeBody,
  NodeChip,
  NodeChipStrip,
  NodeCollapsible,
  NodeField,
  NodeFooter,
  NodeHeader,
  NodeHero,
  NodeFrame,
  NodeShell,
  NodeZone,
  TechField,
  CanvasDockTextarea,
  type NodeLayoutVariant,
} from '@/features/studio/canvas/nodes/NodeChrome'
import {
  CATEGORY_COLORS,
  GEN_NODE_TYPES,
  nodeCategory,
  NODE_TYPE_COLORS,
  outputDataTypeForNode,
  taskTypeForNode,
  type HandleDataType,
  type StudioNodeData,
  type StudioNodeType,
} from '@/features/studio/canvas/types'

type StudioNodeProps = NodeProps & {
  data: StudioNodeData
}

type UpstreamPreview = {
  nodeId: string
  label: string
  url?: string
  dataType?: HandleDataType
}

const TYPE_LABEL_KEYS: Record<StudioNodeType, string> = {
  context: 'canvas.node.context',
  text: 'canvas.node.text',
  prompt: 'canvas.node.prompt',
  upload: 'canvas.node.upload',
  ref_character: 'canvas.node.refCharacter',
  ref_scene: 'canvas.node.refScene',
  gen_image: 'canvas.node.genImage',
  gen_video: 'canvas.node.genVideo',
  gen_audio: 'canvas.node.genAudio',
  register: 'canvas.node.register',
  export: 'canvas.node.export',
  group: 'canvas.node.group',
}

const CATEGORY_LABEL_KEYS = {
  input: 'canvas.category.input',
  generate: 'canvas.category.generate',
  output: 'canvas.category.output',
  container: 'canvas.category.container',
} as const

const GEN_NODE_TYPES_LOCAL = GEN_NODE_TYPES
const GEN_HERO_MAX_HEIGHT = 400
const GEN_CONTROL_MIN_WIDTH = 380
const GEN_LANDSCAPE_WIDTH = 580

function nodeWidth(nodeType: StudioNodeType): number {
  if (nodeType === 'text') return 400
  if (nodeType === 'register') return 300
  if (nodeType === 'export') return 360
  if (nodeType === 'context') return 280
  if (nodeType === 'prompt') return 300
  return 300
}

function parseAspectRatio(ratio: string): { w: number; h: number } | null {
  const parts = ratio.split(':').map(Number)
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null
  return { w: parts[0], h: parts[1] }
}

function previewAspectRatio(nodeType: StudioNodeType, data: StudioNodeData, projectAspect: string): string {
  if (nodeType === 'gen_image' || nodeType === 'gen_video') {
    const param = data.gen?.params?.aspect_ratio
    if (typeof param === 'string' && param.includes(':')) return param
  }
  return projectAspect
}

function genNodeWidth(nodeType: StudioNodeType, aspectRatio: string): number {
  if (nodeType === 'gen_audio') return 460
  const parsed = parseAspectRatio(aspectRatio)
  if (!parsed) return GEN_LANDSCAPE_WIDTH
  const ratio = parsed.w / parsed.h
  let heroWidth: number
  if (ratio >= 1) {
    if (ratio >= 1.5) heroWidth = GEN_LANDSCAPE_WIDTH
    else heroWidth = Math.min(GEN_LANDSCAPE_WIDTH, 480)
  } else {
    heroWidth = Math.round(GEN_HERO_MAX_HEIGHT * ratio)
  }
  return Math.max(GEN_CONTROL_MIN_WIDTH, heroWidth)
}

function layoutForNode(nodeType: StudioNodeType): NodeLayoutVariant {
  if (GEN_NODE_TYPES_LOCAL.includes(nodeType)) return 'gen'
  if (nodeType === 'upload' || nodeType === 'export') return 'media'
  if (nodeType === 'register') return 'output'
  if (nodeType === 'context') return 'compact'
  return 'default'
}

function isTextGen(nodeType: StudioNodeType): boolean {
  return nodeType === 'text'
}

function isGen(nodeType: StudioNodeType): boolean {
  return GEN_NODE_TYPES_LOCAL.includes(nodeType)
}

function genTaskType(nodeType: StudioNodeType): 'image' | 'video' | 'audio' | null {
  return taskTypeForNode(nodeType)
}

function previewAspectClass(nodeType: StudioNodeType, aspectRatio: string): string {
  if (nodeType === 'gen_audio') return 'cn-hero--audio'
  const map: Record<string, string> = {
    '16:9': 'cn-hero--169',
    '9:16': 'cn-hero--916',
    '1:1': 'cn-hero--11',
    '4:3': 'cn-hero--43',
    '3:4': 'cn-hero--34',
  }
  return map[aspectRatio] ?? 'cn-hero--916'
}

function StudioNodeInner({ id, data, selected }: StudioNodeProps) {
  const { t } = useTranslation()
  const ctx = useCanvasNodeContext()
  const {
    aspectRatio,
    imageModels,
    videoModels,
    audioModels,
    textModels,
    onRun,
    onPatch,
    onPatchDraft,
    onFocusUpstream,
    onUploadFile,
    onExportCompose,
    isCreative,
    onOpenPublishWorks,
    onTrimAudio,
  } = ctx
  const nodeType = data.nodeType
  const color = NODE_TYPE_COLORS[nodeType]
  const category = nodeCategory(nodeType)
  const categoryColor = CATEGORY_COLORS[category]
  const isGenNode = isGen(nodeType)
  const nodeAspectRatio = previewAspectRatio(nodeType, data, aspectRatio)
  const width = isGenNode ? genNodeWidth(nodeType, nodeAspectRatio) : nodeWidth(nodeType)
  const layout = layoutForNode(nodeType)
  const isTextGenNode = isTextGen(nodeType)
  const taskType = genTaskType(nodeType)
  const models =
    taskType === 'image'
      ? imageModels
      : taskType === 'video'
        ? videoModels
        : taskType === 'audio'
          ? audioModels
          : isTextGenNode
            ? textModels
            : []

  const primaryParamFields = isGenNode && nodeType !== 'gen_audio'
    ? primaryParamFieldsForNode(nodeType)
    : []
  const advancedParamFields = isGenNode && nodeType !== 'gen_audio'
    ? advancedParamFieldsForNode(nodeType)
    : []
  const status = data.output?.status ?? 'idle'
  const showChrome = selected
  const takes = data.takes ?? []
  const preferred = data.preferredTakeVersion
  const activeTake = takes.find((x) => x.version === preferred) ?? takes[takes.length - 1]
  const previewUrl = resolveMediaDisplayUrl(data.output?.previewUrl ?? activeTake?.previewUrl) || undefined
  const previewDataType: HandleDataType | undefined =
    data.output?.dataType ?? outputDataTypeForNode(nodeType)
  const aspectClass = previewAspectClass(nodeType, nodeAspectRatio)
  const upstream = data.upstreamPreviews ?? []
  const promptPositiveLive = useRef(data.prompt?.positive ?? '')
  const promptNegativeLive = useRef(data.prompt?.negative ?? '')

  useEffect(() => {
    promptPositiveLive.current = data.prompt?.positive ?? ''
    promptNegativeLive.current = data.prompt?.negative ?? ''
  }, [id, data.prompt?.positive, data.prompt?.negative])

  const modelOptions = useMemo(() => buildCanvasModelOptions(models, t), [models, t])

  const statusLabel = useMemo(() => {
    if (status === 'running' || status === 'pending') return t('canvas.status.running')
    if (status === 'succeeded') return t('canvas.status.succeeded')
    if (status === 'failed') return t('canvas.status.failed')
    return t('canvas.status.idle')
  }, [status, t])

  const statusKey =
    status === 'running' || status === 'pending'
      ? 'running'
      : status === 'succeeded'
        ? 'succeeded'
        : status === 'failed'
          ? 'failed'
          : 'idle'

  const inputHandles = handlesOf(nodeType, 'in')
  const outputHandles = useMemo(
    () => (nodeType === 'upload' ? uploadOutputHandles({ data }) : handlesOf(nodeType, 'out')),
    [nodeType, data.upload?.mime, data.upload?.url, data.output?.dataType],
  )
  const updateNodeInternals = useUpdateNodeInternals()

  useEffect(() => {
    updateNodeInternals(id)
  }, [id, selected, isGenNode, width, nodeAspectRatio, inputHandles.length, outputHandles.length, updateNodeInternals])

  function renderHandles(
    handles: HandleSpec[],
    handleType: 'source' | 'target',
    handlePosition: typeof Position.Left | typeof Position.Right,
  ) {
    return handles.map((h, i) => (
      <HandleWrapper
        key={h.id}
        spec={h}
        type={handleType}
        position={handlePosition}
        edgeIndex={i}
        edgeCount={handles.length}
        hoveredZ={20 + i}
        t={t}
      />
    ))
  }

  function renderBody() {
    if (nodeType === 'context') {
      const text = data.context?.prompt?.trim() || data.context?.title || data.context?.ctx || t('canvas.contextEmpty')
      if (!showChrome) {
        return <p className="cn-node-preview cn-node-preview--text whitespace-pre-wrap">{text}</p>
      }
      return (
        <CanvasDockTextarea
          className="cn-field cn-field--flat studio-scrollbar-hidden nodrag nopan nowheel"
          rows={16}
          value={data.context?.prompt ?? ''}
          onCommit={(prompt) =>
            onPatch(id, {
              context: {
                ctx: data.context?.ctx ?? 'shot-frame',
                entityId: data.context?.entityId,
                shotPublicId: data.context?.shotPublicId,
                shotPublicIds: data.context?.shotPublicIds,
                shotSlug: data.context?.shotSlug,
                title: data.context?.title ?? data.label,
                prompt,
                intent: data.context?.intent,
              },
            })
          }
          placeholder={data.context?.title || t('canvas.contextEmpty')}
        />
      )
    }

    if (nodeType === 'text') {
      if (!showChrome) {
        return (
          <div className="cn-node-preview cn-node-preview--text">
            {status === 'running' || status === 'pending' ? (
              <div className="cn-text-output cn-text-output--loading">
                <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
                <span>{t('canvas.status.running')}</span>
              </div>
            ) : (
              <p className="whitespace-pre-wrap">{data.text?.content?.trim() || t('canvas.textOutputPlaceholder')}</p>
            )}
          </div>
        )
      }
      return (
        <>
          {upstream.length > 0 ? (
            <NodeZone label={t('canvas.section.inputs')} compact>
              <UpstreamChipStrip previews={upstream} onFocus={onFocusUpstream} />
            </NodeZone>
          ) : null}
          {status === 'running' || status === 'pending' ? (
            <div className="cn-text-output cn-text-output--loading">
              <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
              <span>{t('canvas.status.running')}</span>
            </div>
          ) : (
            <TechField
              multiline
              rows={6}
              className="cn-field--flat nodrag nopan"
              value={data.text?.content ?? ''}
              onChange={(e) =>
                onPatch(id, {
                  text: {
                    content: e.target.value,
                    instruction: data.text?.instruction ?? '',
                  },
                })
              }
              placeholder={t('canvas.textOutputPlaceholder')}
            />
          )}
        </>
      )
    }

    if (nodeType === 'prompt') {
      if (!showChrome) {
        const positive = data.prompt?.positive?.trim()
        return (
          <div className="cn-node-preview cn-node-preview--text">
            <p className="whitespace-pre-wrap">{positive || t('canvas.promptPositivePlaceholder')}</p>
          </div>
        )
      }
      return (
        <>
          <TechField
            multiline
            rows={4}
            className="cn-field--flat"
            value={data.prompt?.positive ?? ''}
            onChange={(e) =>
              onPatch(id, {
                prompt: { ...data.prompt, positive: e.target.value, negative: data.prompt?.negative ?? '' },
              })
            }
            placeholder={t('canvas.promptPositivePlaceholder')}
          />
          <NodeCollapsible label={t('canvas.section.negative')}>
            <TechField
              multiline
              rows={2}
              className="cn-field--flat cn-field--muted"
              value={data.prompt?.negative ?? ''}
              onChange={(e) =>
                onPatch(id, {
                  prompt: { ...data.prompt, positive: data.prompt?.positive ?? '', negative: e.target.value },
                })
              }
              placeholder={t('canvas.promptNegativePlaceholder')}
            />
          </NodeCollapsible>
        </>
      )
    }

    if (nodeType === 'upload') {
      return (
        <UploadBody
          url={data.upload?.url}
          mime={data.upload?.mime}
          filename={data.upload?.filename}
          showChrome={showChrome}
          onPick={(file) => onUploadFile(id, file)}
          onClear={() => onPatch(id, { upload: {}, output: { status: 'idle' } })}
          t={t}
        />
      )
    }

    if (nodeType === 'ref_character' || nodeType === 'ref_scene') {
      const ref = nodeType === 'ref_character' ? data.refCharacter : data.refScene
      const setter = (patch: { urls?: string[]; name?: string; hint?: string }) => {
        if (nodeType === 'ref_character') {
          const base = data.refCharacter ?? { urls: [], name: '', hint: '' }
          onPatch(id, { refCharacter: { ...base, ...patch } })
        } else {
          const base = data.refScene ?? { urls: [], name: '', hint: '' }
          onPatch(id, { refScene: { ...base, ...patch } })
        }
      }
      return (
        <>
          {!showChrome ? (
            <div className="cn-node-preview cn-node-preview--media">
              {(ref?.urls ?? []).length > 0 ? (
                <div className="cn-ref-grid">
                  {(ref?.urls ?? []).map((u, i) => (
                    <div key={i} className="cn-ref-thumb">
                      <img src={u} alt="" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cn-node-preview--empty">{t('canvas.refAddImage')}</div>
              )}
            </div>
          ) : (
            <>
          <NodeZone label={t('canvas.section.references')} compact>
            <div className="cn-ref-grid">
              {(ref?.urls ?? []).map((u, i) => (
                <div key={i} className="cn-ref-thumb group">
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setter({ urls: (ref?.urls ?? []).filter((_, j) => j !== i) })}
                    className="cn-ref-thumb__remove"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <label className="cn-ref-add cursor-pointer" title={t('canvas.refAddImage')}>
                <UploadIcon className="h-4 w-4" />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onUploadFile(id, e.target.files?.[0] ?? null, 'image')}
                />
              </label>
            </div>
          </NodeZone>
          <NodeZone label={t('canvas.section.identity')} compact>
            <div className="cn-ref-fields">
              <NodeField label={t('canvas.refNamePlaceholder')}>
                <TechField
                  value={ref?.name ?? ''}
                  onChange={(e) => setter({ name: e.target.value })}
                  placeholder={t('canvas.refNamePlaceholder')}
                />
              </NodeField>
              <NodeField label={t('canvas.refHintPlaceholder')}>
                <TechField
                  value={ref?.hint ?? ''}
                  onChange={(e) => setter({ hint: e.target.value })}
                  placeholder={t('canvas.refHintPlaceholder')}
                />
              </NodeField>
            </div>
          </NodeZone>
            </>
          )}
        </>
      )
    }

    if (isGenNode && nodeType === 'gen_audio') {
      return (
        <AudioGenBody
          id={id}
          data={data}
          showChrome={showChrome}
          audioModels={audioModels}
          previewUrl={previewUrl}
          upstream={upstream}
          nodeTitle={t(TYPE_LABEL_KEYS[nodeType], { defaultValue: data.label })}
          onPatch={onPatch}
          onRun={() =>
            onRun(id, {
              promptPositive: promptPositiveLive.current,
              promptNegative: promptNegativeLive.current,
            })
          }
          onTrimAudio={onTrimAudio}
          runDisabled={status === 'running' || !data.gen?.modelId}
          runRunning={status === 'running' || status === 'pending'}
          t={t}
        />
      )
    }

    if (isGenNode) {
      return (
        <GenBody
          id={id}
          data={data}
          showChrome={showChrome}
          primaryFields={primaryParamFields}
          advancedFields={advancedParamFields}
          modelOptions={modelOptions}
          previewUrl={previewUrl}
          previewDataType={previewDataType}
          aspectClass={aspectClass}
          upstream={upstream}
          takes={takes}
          nodeTitle={t(TYPE_LABEL_KEYS[nodeType], { defaultValue: data.label })}
          onPatch={onPatch}
          onRun={(opts) =>
            onRun(id, {
              promptPositive: promptPositiveLive.current,
              promptNegative: promptNegativeLive.current,
              genParams:
                opts?.genParams ??
                resolvedGenParamsForSubmit(data.gen?.params, [
                  ...primaryParamFields,
                  ...advancedParamFields,
                ]),
            })
          }
          runDisabled={status === 'running' || !data.gen?.modelId}
          runRunning={status === 'running' || status === 'pending'}
          t={t}
        />
      )
    }

    if (nodeType === 'register') {
      if (!showChrome) {
        return (
          <div className="cn-node-preview cn-node-preview--text">
            {upstream.length > 0 ? (
              <div className="mb-2">
                <UpstreamChipStrip previews={upstream} onFocus={onFocusUpstream} />
              </div>
            ) : null}
            <p className="line-clamp-4">{data.register?.name?.trim() || t('canvas.registerName')}</p>
          </div>
        )
      }
      return (
        <>
          {upstream.length > 0 ? (
            <NodeZone label={t('canvas.section.inputs')} compact>
              <UpstreamChipStrip previews={upstream} onFocus={onFocusUpstream} />
            </NodeZone>
          ) : null}
          {isCreative ? (
            <p className="cn-export-hint">{t('canvas.registerCreativeHint')}</p>
          ) : null}
          <NodeZone label={t('canvas.section.output')}>
            <div className="cn-register-row">
              <NodeField label={t('canvas.registerName')}>
                <TechField
                  value={data.register?.name ?? ''}
                  onChange={(e) => onPatch(id, { register: { ...data.register, name: e.target.value } })}
                  placeholder={t('canvas.registerName')}
                />
              </NodeField>
              <NodeField label={t('canvas.section.assetKind')}>
                <ProtoDropdown
                  compact
                  portal
                  className="cn-gen-controls__field"
                  value={data.register?.kind ?? 'misc'}
                  onValueChange={(kind) => onPatch(id, { register: { ...data.register, kind } })}
                  options={[
                    { value: 'character_ref', label: t('canvas.registerKind.characterRef') },
                    { value: 'location_ref', label: t('canvas.registerKind.locationRef') },
                    { value: 'storyboard', label: t('canvas.registerKind.storyboard') },
                    { value: 'take_video', label: t('canvas.registerKind.takeVideo') },
                    { value: 'prop', label: t('canvas.registerKind.prop') },
                    { value: 'misc', label: t('canvas.registerKind.misc') },
                  ]}
                />
              </NodeField>
            </div>
          </NodeZone>
        </>
      )
    }

    if (nodeType === 'export') {
      const exp = data.export
      const preview = resolveMediaDisplayUrl(exp?.outputUrl || data.output?.previewUrl) || undefined
      const hasVideo = Boolean(preview) || upstream.some((p) => p.dataType === 'video' && !!p.url)
      const hasAudio = upstream.some((p) => p.dataType === 'audio' && !!p.url)
      const hasFinal = Boolean(preview)
      const canCompose =
        !!onExportCompose &&
        exp?.status !== 'running' &&
        (hasAudio ? hasVideo : !hasFinal && hasVideo)
      const filmTitle = exp?.title ?? data.label
      const hint =
        exp?.status === 'failed' && exp.error
          ? exp.error
          : exp?.status === 'running'
            ? t('canvas.exportRunning')
            : hasFinal
              ? t('canvas.exportDone')
              : hasVideo && !hasAudio
                ? t('canvas.exportHintVideoOnly')
                : t('canvas.exportHint')
      if (!showChrome) {
        return (
          <div className="cn-export">
            <NodeHero className="cn-hero--169 cn-node-preview--media" empty={!preview ? t('canvas.exportEmpty') : undefined}>
              {preview ? (
                <video src={preview} className="max-h-full max-w-full object-contain" muted />
              ) : null}
            </NodeHero>
            <p className="cn-export__title-preview">{filmTitle || t('canvas.exportTitle')}</p>
          </div>
        )
      }
      return (
        <div className="cn-export">
          <NodeHero className="cn-hero--169" empty={!preview ? t('canvas.exportEmpty') : undefined}>
            {preview ? (
              <video src={preview} className="max-h-full max-w-full object-contain" controls muted />
            ) : null}
          </NodeHero>
          <NodeZone label={t('canvas.exportTitle')}>
            <TechField
              value={filmTitle}
              onChange={(e) =>
                onPatch(id, {
                  label: e.target.value,
                  export: { ...exp, status: exp?.status ?? 'idle', title: e.target.value },
                })
              }
              placeholder={t('canvas.exportTitlePlaceholder')}
            />
          </NodeZone>
          {upstream.length > 0 ? (
            <NodeZone label={t('canvas.section.inputs')} compact>
              <UpstreamChipStrip previews={upstream} onFocus={onFocusUpstream} />
            </NodeZone>
          ) : null}
          <p className={clsx('cn-export-hint', exp?.status === 'failed' && 'cn-export-hint--error')}>{hint}</p>
          <div className="cn-export__actions nodrag nopan nowheel">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={exp?.status === 'running'}
              onClick={() => onUploadFile(id, null, 'video')}
              className="cn-export__btn"
            >
              <UploadIcon className="h-3.5 w-3.5" />
              <span>{t('canvas.exportUpload')}</span>
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={!canCompose}
              onClick={() => onExportCompose?.(id)}
              className="cn-export__btn cn-export__btn--primary"
            >
              {exp?.status === 'running' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              <span>{hasAudio ? t('canvas.exportCompose') : t('canvas.exportAdopt')}</span>
            </Button>
          </div>
          {isCreative ? (
            <div className="cn-export__publish nodrag nopan nowheel">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!hasFinal}
                onClick={() => onOpenPublishWorks?.(id)}
                className="cn-export__btn"
              >
                <UploadIcon className="h-3.5 w-3.5" />
                <span>{t('timeline.syncWorks')}</span>
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={!hasFinal}
                onClick={() => onOpenPublishWorks?.(id)}
                className="cn-export__btn cn-export__btn--publish"
              >
                <Rocket className="h-3.5 w-3.5" />
                <span>{t('timeline.publishWorks')}</span>
              </Button>
            </div>
          ) : null}
        </div>
      )
    }

    return null
  }

  const bodyFlush = layout === 'gen' || layout === 'media' || layout === 'output'
  const nodeTitle = t(TYPE_LABEL_KEYS[nodeType], { defaultValue: data.label })
  const isRunning = status === 'running' || status === 'pending'

  const shellProps = {
    width,
    layout,
    selected,
    running: isRunning,
    accentColor: color,
    categoryColor,
  }

  const shellChildren = (
    <>
      <NodeHeader
        categoryLabel={t(CATEGORY_LABEL_KEYS[category])}
        categoryColor={categoryColor}
        title={t(TYPE_LABEL_KEYS[nodeType], { defaultValue: data.label })}
        accentColor={color}
        status={statusKey}
        statusLabel={isGenNode || isTextGenNode ? statusLabel : undefined}
      />

      <NodeBody flush={bodyFlush} scroll={!bodyFlush}>{renderBody()}</NodeBody>

      {isGenNode ? (
        <NodeFooter>
          <div className="cn-foot__meta">
            <span>{activeTake ? `v${activeTake.version}` : 'v0'}</span>
            {activeTake?.cost ? (
              <>
                <span className="cn-foot__dot">·</span>
                <span>{t('canvas.modelCost', { cost: activeTake.cost })}</span>
              </>
            ) : null}
          </div>
        </NodeFooter>
      ) : null}

      {renderHandles(inputHandles, 'target', Position.Left)}
      {renderHandles(outputHandles, 'source', Position.Right)}
    </>
  )

  if (isGenNode) {
    return (
      <div
        className={clsx('cn-node-stack', selected && 'cn-node-stack--selected')}
        style={
          {
            width,
            '--cn-accent': color,
            '--cn-category': categoryColor,
            '--cn-prompt-dock-width': `${width * 2}px`,
          } as CSSProperties
        }
      >
        <NodeFrame
          fullWidth
          width={width}
          selected={selected}
          accentColor={color}
          categoryColor={categoryColor}
          title={nodeTitle}
        >
          <NodeShell {...shellProps}>{shellChildren}</NodeShell>
        </NodeFrame>
        {selected ? (
          <GenPromptDock
            id={id}
            data={data}
            onPatchDraft={onPatchDraft}
            positiveLiveRef={promptPositiveLive}
            negativeLiveRef={promptNegativeLive}
            t={t}
          />
        ) : null}
      </div>
    )
  }

  if (isTextGenNode) {
    const textModelOptions = buildCanvasModelOptions(textModels, t)
    return (
      <div
        className={clsx('cn-node-stack', selected && 'cn-node-stack--selected')}
        style={
          {
            width,
            '--cn-accent': color,
            '--cn-category': categoryColor,
            '--cn-prompt-dock-width': `${width * 2}px`,
          } as CSSProperties
        }
      >
        <NodeFrame
          fullWidth
          width={width}
          selected={selected}
          accentColor={color}
          categoryColor={categoryColor}
          title={nodeTitle}
        >
          <NodeShell {...shellProps}>{shellChildren}</NodeShell>
        </NodeFrame>
        {selected ? (
          <TextExternalDock
            id={id}
            data={data}
            modelOptions={textModelOptions}
            onPatchDraft={onPatchDraft}
            onPatch={onPatch}
            onRun={onRun}
            runDisabled={status === 'running' || status === 'pending' || !data.gen?.modelId}
            runRunning={status === 'running' || status === 'pending'}
            t={t}
          />
        ) : null}
      </div>
    )
  }

  return (
    <NodeFrame width={width} selected={selected} accentColor={color} categoryColor={categoryColor} title={nodeTitle}>
      <NodeShell {...shellProps}>{shellChildren}</NodeShell>
    </NodeFrame>
  )
}

function HandleWrapper({
  spec,
  type,
  position,
  edgeIndex,
  edgeCount,
  hoveredZ,
  t,
}: {
  spec: HandleSpec
  type: 'source' | 'target'
  position: Position
  edgeIndex: number
  edgeCount: number
  hoveredZ: number
  t: (k: string, opts?: Record<string, unknown>) => string
}) {
  const [hovered, setHovered] = useState(false)
  const c = handleColor(spec.dataType)
  const role = type === 'target' ? 'in' : 'out'
  const isLeft = type === 'target'
  const desc = t(handleTooltipKey(spec, role), { defaultValue: spec.dataType })
  const typeLabel = t(`canvas.handle.type.${spec.dataType}`, { defaultValue: spec.dataType })
  const roleLabel = isLeft ? t('canvas.handle.roleIn') : t('canvas.handle.roleOut')
  const shapeClass =
    spec.dataType === 'image'
      ? 'cn-handle--square'
      : spec.dataType === 'video'
        ? 'cn-handle--diamond'
        : spec.dataType === 'ctx'
          ? 'cn-handle--dashed'
          : 'cn-handle--circle'

  const offsetY = handleEdgeOffsetY(edgeIndex, edgeCount)
  const hostStyle: CSSProperties = {
    position: 'absolute',
    top: `calc(50% + ${offsetY}px)`,
    width: 0,
    height: 0,
    zIndex: hovered ? hoveredZ + 10 : 10 + edgeIndex,
    ...(isLeft ? { left: 0 } : { right: 0 }),
  }

  return (
    <div
      className={clsx('cn-handle-host', isLeft ? 'cn-handle-host--left' : 'cn-handle-host--right')}
      style={hostStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Handle
        id={spec.id}
        type={type}
        position={position}
        style={
          {
            background: '#0c0c10',
            borderColor: c,
            color: c,
            '--handle-color': c,
          } as CSSProperties
        }
        className={clsx('cn-handle', shapeClass)}
        aria-label={desc}
      />
      {hovered ? (
        <div className={clsx('cn-handle-tip', isLeft ? 'cn-handle-tip--left' : 'cn-handle-tip--right')} role="tooltip">
          <div className="cn-handle-tip__head">
            <span className="cn-handle-tip__type" style={{ color: c, borderColor: c }}>
              {typeLabel}
            </span>
            <span className="cn-handle-tip__role">{roleLabel}</span>
          </div>
          <p className="cn-handle-tip__desc">{desc}</p>
        </div>
      ) : null}
    </div>
  )
}

function UpstreamChipStrip({
  previews,
  onFocus,
}: {
  previews: UpstreamPreview[]
  onFocus: (nodeId: string) => void
}) {
  return (
    <NodeChipStrip>
      {previews.map((p) => (
        <NodeChip
          key={p.nodeId}
          label={p.label}
          sublabel={p.dataType ?? 'ref'}
          color={p.dataType ? handleColor(p.dataType) : undefined}
          onClick={() => onFocus(p.nodeId)}
          thumbnail={
            p.url ? (
              <MediaKindThumb
                url={p.url}
                mediaKind={p.dataType ?? undefined}
                className="h-full w-full"
              />
            ) : (
              '—'
            )
          }
        />
      ))}
    </NodeChipStrip>
  )
}

function GenBody({
  id,
  data,
  showChrome = false,
  primaryFields,
  advancedFields,
  modelOptions,
  previewUrl,
  previewDataType,
  aspectClass,
  upstream,
  takes,
  nodeTitle,
  onPatch,
  onRun,
  runDisabled,
  runRunning,
  t,
}: {
  id: string
  data: StudioNodeData
  showChrome?: boolean
  primaryFields: ModelParamField[]
  advancedFields: ModelParamField[]
  modelOptions: ProtoDropdownOption[]
  previewUrl?: string
  previewDataType?: HandleDataType
  aspectClass: string
  upstream: UpstreamPreview[]
  takes: NonNullable<StudioNodeData['takes']>
  nodeTitle: string
  onPatch: (id: string, patch: Partial<StudioNodeData>) => void
  onRun: (opts?: { genParams?: Record<string, string | number> }) => void
  runDisabled?: boolean
  runRunning?: boolean
  t: (k: string, opts?: Record<string, unknown>) => string
}) {
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [previewIndex, setPreviewIndex] = useState(0)

  const imagePreviewUrls = useMemo(() => {
    if (previewDataType !== 'image') return []
    const fromTakes = takes.map((tk) => tk.previewUrl).filter((u): u is string => !!u)
    if (fromTakes.length) return fromTakes
    const raw =
      data.output?.previewUrl ??
      takes.find((x) => x.version === data.preferredTakeVersion)?.previewUrl ??
      takes[takes.length - 1]?.previewUrl
    return raw ? [raw] : []
  }, [takes, previewDataType, data.output?.previewUrl, data.preferredTakeVersion])

  const openImagePreview = (url?: string) => {
    if (!url || previewDataType !== 'image') return
    setPreviewUrls(imagePreviewUrls)
    const resolvedList = imagePreviewUrls.map((u) => resolveMediaDisplayUrl(u))
    setPreviewIndex(Math.max(0, resolvedList.indexOf(url)))
  }

  const setGen = (patch: Partial<NonNullable<StudioNodeData['gen']>>) =>
    onPatch(id, { gen: { modelId: data.gen?.modelId ?? '', params: data.gen?.params ?? {}, ...patch } })

  const fireRun = () => {
    onRun({
      genParams: resolvedGenParamsForSubmit(data.gen?.params, [...primaryFields, ...advancedFields]),
    })
  }

  const renderParamField = (field: ModelParamField) => {
    const val = data.gen?.params?.[field.key] ?? field.default
    const label = t(field.labelKey, { defaultValue: field.key })
    return (
      <div key={field.key} className="cn-gen-controls__cell">
        <span className="cn-gen-controls__label">{label}</span>
        {field.type === 'select' && field.options ? (
          <ProtoDropdown
            compact
            portal
            className="cn-gen-controls__field"
            value={String(val)}
            onValueChange={(v) => {
              const next: Record<string, string | number> = { ...data.gen?.params, [field.key]: v }
              if (field.key === 'duration') {
                delete (next as Record<string, unknown>).duration_sec
                delete (next as Record<string, unknown>).durationSec
              }
              setGen({ params: next })
            }}
            options={field.options.map((o) => ({
              value: o.value,
              label: t(o.labelKey, { defaultValue: o.value }),
            }))}
            aria-label={label}
          />
        ) : field.type === 'number' ? (
          <ProtoNumberInput
            className="cn-gen-controls__field cn-gen-controls__number"
            value={Number(val)}
            min={field.min}
            max={field.max}
            onValueChange={(n) => setGen({ params: { ...data.gen?.params, [field.key]: n } })}
            aria-label={label}
          />
        ) : field.type === 'text' ? (
          <ProtoInput
            className="cn-gen-controls__field"
            value={String(val ?? '')}
            placeholder={label}
            onChange={(e) => setGen({ params: { ...data.gen?.params, [field.key]: e.target.value } })}
            aria-label={label}
          />
        ) : null}
      </div>
    )
  }

  const previewOnly = !showChrome
  const genError =
    data.output?.status === 'failed' && data.output?.error
      ? formatResolvedApiError(t, simplifyAITaskErrorMessage(data.output.error, 'canvas.errors.generateFailed'))
      : undefined

  return (
    <div className="cn-gen-layout">
      <NodeHero
        className={clsx('cn-hero--gen', aspectClass, genError && 'cn-hero--error')}
        empty={!previewUrl ? (genError ?? t('canvas.previewEmpty')) : undefined}
      >
        {previewUrl ? (
          previewDataType === 'audio' ? (
            <AudioWavePlayer
              variant="hero"
              src={previewUrl}
              interactiveClassName="nodrag nopan nowheel"
            />
          ) : previewDataType === 'video' ? (
            <video src={previewUrl} className="cn-hero__media" controls={showChrome} muted />
          ) : previewOnly ? (
            <img src={previewUrl} alt="" className="cn-hero__media" draggable={false} />
          ) : (
            <Button
              type="button"
              variant="ghost"
              className="cn-hero__media-btn nodrag nopan h-auto p-0 transition hover:opacity-95"
              onClick={() => openImagePreview(previewUrl)}
              aria-label={t('bible.imagePreview')}
            >
              <img src={previewUrl} alt="" className="cn-hero__media cn-hero__media--zoomable" />
            </Button>
          )
        ) : null}
        {showChrome && upstream.length > 0 ? (
          <div className="cn-gen-upstream">
            <NodeChipStrip>
              {upstream.map((p) => (
                <NodeChip
                  key={p.nodeId}
                  label={p.label}
                  sublabel={p.dataType ?? 'ref'}
                  color={p.dataType ? handleColor(p.dataType) : undefined}
                  thumbnail={
                    p.url ? (
                      p.dataType === 'audio' ? (
                        <span>♪</span>
                      ) : (
                        <img src={resolveMediaDisplayUrl(p.url)} alt="" />
                      )
                    ) : (
                      '—'
                    )
                  }
                />
              ))}
            </NodeChipStrip>
          </div>
        ) : null}
      </NodeHero>

      {showChrome ? (
      <div className="cn-gen-controls nodrag nopan nowheel">
        {primaryFields.length > 0 ? (
          <div className="cn-gen-controls__grid cn-gen-controls__grid--primary">
            {primaryFields.map(renderParamField)}
          </div>
        ) : null}
        {advancedFields.length > 0 ? (
          <details className="cn-gen-controls__advanced">
            <summary className="cn-gen-controls__advanced-summary">
              <span className="cn-gen-controls__advanced-title">{t('canvas.params.more')}</span>
              <span className="cn-gen-controls__advanced-toggle">
                <span className="cn-gen-controls__advanced-toggle-label cn-gen-controls__advanced-toggle-label--expand">
                  {t('canvas.params.expand')}
                </span>
                <span className="cn-gen-controls__advanced-toggle-label cn-gen-controls__advanced-toggle-label--collapse">
                  {t('canvas.params.collapse')}
                </span>
                <ChevronDown className="cn-gen-controls__advanced-chevron" strokeWidth={2.25} />
              </span>
            </summary>
            <div className="cn-gen-controls__grid cn-gen-controls__grid--advanced">
              {advancedFields.map(renderParamField)}
            </div>
          </details>
        ) : null}
        <div className="cn-gen-controls__footer">
          <ProtoDropdown
            compact
            portal
            className="cn-gen-controls__model"
            value={data.gen?.modelId ?? ''}
            onValueChange={(modelId) => setGen({ modelId })}
            options={modelOptions}
            disabled={!modelOptions.length}
            placeholder={t('canvas.section.model')}
          />
          <Button type="button" variant="default" size="sm" disabled={runDisabled} onClick={fireRun} className="cn-run cn-run--primary cn-gen-controls__run">
            {runRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            <span>{t('canvas.run')}</span>
          </Button>
        </div>
      </div>
      ) : null}
      {showChrome && !modelOptions.length ? <p className="cn-gen-controls__warn">{t('canvas.noModel')}</p> : null}
      <ImagePreviewModal
        open={previewUrls.length > 0}
        urls={previewUrls}
        index={previewIndex}
        title={nodeTitle}
        onClose={() => {
          setPreviewUrls([])
          setPreviewIndex(0)
        }}
        onIndexChange={setPreviewIndex}
      />
    </div>
  )
}

function stopCanvasPointer(e: React.SyntheticEvent) {
  e.stopPropagation()
}

function GenPromptDock({
  id,
  data,
  onPatchDraft,
  positiveLiveRef,
  negativeLiveRef,
  t,
}: {
  id: string
  data: StudioNodeData
  onPatchDraft: CanvasNodeContextValue['onPatchDraft']
  positiveLiveRef: React.MutableRefObject<string>
  negativeLiveRef: React.MutableRefObject<string>
  t: (k: string, opts?: Record<string, unknown>) => string
}) {
  const isAudio = data.nodeType === 'gen_audio'
  const audioTab = isAudio ? getActiveAudioTab(data) : null
  const positivePlaceholder = isAudio
    ? t(audioPromptPlaceholderKey(audioTab!, data.gen?.params))
    : t('canvas.promptPositivePlaceholder')
  const promptLabel = !isAudio
    ? t('canvas.section.prompt')
    : audioTab === 'music'
      ? String(data.gen?.params?.music_mode ?? 'describe') === 'lyrics'
        ? t('canvas.audioTab.promptLabelLyrics')
        : t('canvas.audioTab.promptLabelMusic')
      : t('canvas.audioTab.promptLabel')

  return (
    <div
      className="cn-gen-prompt-external nodrag nopan nowheel"
      onPointerDown={stopCanvasPointer}
      onMouseDown={stopCanvasPointer}
      onDoubleClick={stopCanvasPointer}
    >
      <label className="cn-gen-prompt-external__label">{promptLabel}</label>
      <CanvasDockTextarea
        rows={6}
        className="cn-gen-prompt-external__field"
        value={data.prompt?.positive ?? ''}
        liveRef={positiveLiveRef}
        onCommit={(positive) =>
          onPatchDraft(id, (prev) => ({
            prompt: { positive, negative: prev.prompt?.negative ?? '' },
          }))
        }
        placeholder={positivePlaceholder}
      />
      {!isAudio ? (
        <details className="cn-gen-prompt-external__neg">
          <summary className="cn-gen-prompt-external__neg-summary">{t('canvas.section.negative')}</summary>
          <CanvasDockTextarea
            rows={4}
            className="cn-gen-prompt-external__neg-field cn-field--muted"
            value={data.prompt?.negative ?? ''}
            liveRef={negativeLiveRef}
            onCommit={(negative) =>
              onPatchDraft(id, (prev) => ({
                prompt: { positive: prev.prompt?.positive ?? '', negative },
              }))
            }
            placeholder={t('canvas.promptNegativePlaceholder')}
          />
        </details>
      ) : null}
    </div>
  )
}

function TextExternalDock({
  id,
  data,
  modelOptions,
  onPatchDraft,
  onPatch,
  onRun,
  runDisabled,
  runRunning,
  t,
}: {
  id: string
  data: StudioNodeData
  modelOptions: ProtoDropdownOption[]
  onPatchDraft: CanvasNodeContextValue['onPatchDraft']
  onPatch: (id: string, patch: Partial<StudioNodeData>) => void
  onRun: (nodeId: string, opts?: { textInstruction?: string; promptPositive?: string; promptNegative?: string }) => void
  runDisabled?: boolean
  runRunning?: boolean
  t: (k: string, opts?: Record<string, unknown>) => string
}) {
  const instructionLive = useRef(data.text?.instruction ?? '')

  const setGen = (patch: Partial<NonNullable<StudioNodeData['gen']>>) =>
    onPatch(id, { gen: { modelId: data.gen?.modelId ?? '', params: data.gen?.params ?? {}, ...patch } })

  return (
    <div
      className="cn-gen-prompt-external nodrag nopan nowheel"
      onPointerDown={stopCanvasPointer}
      onMouseDown={stopCanvasPointer}
      onDoubleClick={stopCanvasPointer}
    >
      <label className="cn-gen-prompt-external__label">{t('canvas.section.instruction')}</label>
      <CanvasDockTextarea
        rows={5}
        className="cn-gen-prompt-external__field"
        value={data.text?.instruction ?? ''}
        liveRef={instructionLive}
        onCommit={(instruction) =>
          onPatchDraft(id, (prev) => ({
            text: { content: prev.text?.content ?? '', instruction },
          }))
        }
        placeholder={t('canvas.textInstructionPlaceholder')}
      />
      <div className="cn-gen-controls__footer">
        <ProtoDropdown
          compact
          portal
          className="cn-gen-controls__model"
          value={data.gen?.modelId ?? ''}
          onValueChange={(modelId) => setGen({ modelId })}
          options={modelOptions}
          disabled={!modelOptions.length}
          placeholder={t('canvas.section.model')}
        />
        <Button
          type="button"
          variant="default"
          size="sm"
          disabled={runDisabled}
          onClick={() => onRun(id, { textInstruction: instructionLive.current })}
          className="cn-run cn-run--primary cn-gen-controls__run"
        >
          {runRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          <span>{t('canvas.run')}</span>
        </Button>
      </div>
      {!modelOptions.length ? <p className="cn-gen-controls__warn">{t('canvas.noModel')}</p> : null}
    </div>
  )
}

function UploadBody({
  url,
  mime,
  filename,
  showChrome = false,
  onPick,
  onClear,
  t,
}: {
  url?: string
  mime?: string
  filename?: string
  showChrome?: boolean
  onPick: (file: File | null) => void
  onClear: () => void
  t: (k: string, opts?: Record<string, unknown>) => string
}) {
  const isAudio = mime?.startsWith('audio/')
  const isVideo = mime?.startsWith('video/')

  if (!showChrome) {
    if (!url) {
      return <div className="cn-node-preview cn-node-preview--empty">{t('canvas.uploadHint')}</div>
    }
    return (
      <NodeHero className="cn-node-preview--media" empty={undefined}>
        {isVideo ? (
          <video src={url} className="max-h-48 w-full object-contain" muted />
        ) : isAudio ? (
          <AudioWavePlayer variant="hero" src={url} interactiveClassName="nodrag nopan nowheel" />
        ) : (
          <img src={url} alt="" className="max-h-48 w-full object-contain" />
        )}
      </NodeHero>
    )
  }

  if (!url) {
    return (
      <label className="cn-upload-drop">
        <UploadIcon className="h-6 w-6 text-slate-500" />
        <span>{t('canvas.uploadHint')}</span>
        <input
          type="file"
          accept="image/*,video/*,audio/*"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0] ?? null)}
        />
      </label>
    )
  }

  return (
    <>
      <NodeHero empty={undefined}>
        {isVideo ? (
          <video src={url} className="max-h-48 w-full object-contain" controls muted />
        ) : isAudio ? (
          <AudioWavePlayer variant="hero" src={url} interactiveClassName="nodrag nopan nowheel" />
        ) : (
          <img src={url} alt="" className="max-h-48 w-full object-contain" />
        )}
      </NodeHero>
      <div className="cn-upload-meta">
        <span className="truncate">{filename ?? t('canvas.uploaded')}</span>
        <Button type="button" variant="ghost" size="icon-xs" onClick={onClear} aria-label={t('common.delete')}>
          <X className="h-3 w-3" />
        </Button>
      </div>
    </>
  )
}

export const StudioNode = memo(StudioNodeInner)
